// 📁 src/services/useLocationService.js
import { useState, useEffect } from "react";

export default function useLocationService(autoFetch = true) {
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [permission, setPermission] = useState("prompt"); // 'granted' | 'denied' | 'prompt'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Check permission status (modern browsers)
  const checkPermission = async () => {
    if (!navigator.permissions) {
      console.warn("Permissions API not supported in this browser.");
      return;
    }

    try {
      const result = await navigator.permissions.query({ name: "geolocation" });
      setPermission(result.state);

      result.onchange = () => {
        setPermission(result.state);
      };
    } catch (err) {
      console.error("Permission check error:", err);
    }
  };

  // ✅ Fetch location (after permission)
  const getLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!("geolocation" in navigator)) {
        throw new Error("Geolocation is not supported by your browser.");
      }

      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        })
      );

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      setLocation({ lat, lng });
      return { lat, lng };
    } catch (err) {
      let message = "Unable to fetch location.";
      if (err.code === 1) message = "Permission denied. Please allow location access.";
      else if (err.code === 2) message = "Position unavailable.";
      else if (err.code === 3) message = "Request timed out.";
      setError(message);
      console.error("Location error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch if allowed
  useEffect(() => {
    checkPermission();

    if (autoFetch) {
      getLocation();
    }
  }, []);

  return {
    location,
    loading,
    error,
    permission,
    getLocation,
    checkPermission,
  };
}
